import React, { useState } from 'react';
import {
    useDataProvider,
    useNotify,
    useRefresh,
    DateField,
    BooleanField,
    FunctionField,
} from 'react-admin';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Typography,
    Chip,
    Box,
    CircularProgress,
} from '@material-ui/core';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    GetApp as DownloadIcon,
    Visibility as ViewIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import feathersClient from '../../rest/feathersClient';
import settings from '../../settings';

const useStyles = makeStyles(theme => ({
    addButton: {
        marginBottom: theme.spacing(2),
    },
    table: {
        marginTop: theme.spacing(2),
    },
    fileInput: {
        display: 'none',
    },
    uploadButton: {
        marginTop: theme.spacing(1),
    },
    actionButtons: {
        display: 'flex',
        gap: theme.spacing(1),
    },
}));

interface DocumentType {
    id: number;
    name: string;
    code: string;
    description?: string;
    isActive: boolean;
}

interface UserDocument {
    id?: number;
    userId: number;
    documentType: string;
    documentNumber: string;
    expiryDate: string;
    fileUrl?: string;
    fileName?: string;
    fileSize?: number;
    description?: string;
    isVerified?: boolean;
    verifiedAt?: string;
    createdAt?: string;
    updatedAt?: string;
}

interface Props {
    record: {
        id: number;
    };
    showAddButton?: boolean;
}

const UserDocumentsList: React.FC<Props> = ({
    record,
    showAddButton = true,
}) => {
    const classes = useStyles();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();

    const [documents, setDocuments] = useState<UserDocument[]>([]);
    const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingDocument, setEditingDocument] = useState<UserDocument | null>(
        null
    );
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [saving, setSaving] = useState(false);

    const [formData, setFormData] = useState<UserDocument>({
        userId: record.id,
        documentType: '',
        documentNumber: '',
        expiryDate: '',
        description: '',
    });

    // Load documents and document types on mount
    React.useEffect(() => {
        loadDocuments();
        loadDocumentTypes();
    }, [record.id]);

    const loadDocuments = async () => {
        setLoading(true);
        try {
            const { data } = await dataProvider.getList('user-documents', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'createdAt', order: 'DESC' },
                filter: { userId: record.id },
            });
            setDocuments(data);
        } catch (error) {
            notify('Error loading documents', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const loadDocumentTypes = async () => {
        try {
            const { data } = await dataProvider.getList('document-types', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'name', order: 'ASC' },
                filter: { isActive: true },
            });
            setDocumentTypes(data);
        } catch (error) {
            console.error('Error loading document types:', error);
            notify('Error loading document types', { type: 'error' });
        }
    };

    const handleOpenDialog = (document?: UserDocument) => {
        if (document) {
            setEditingDocument(document);
            setFormData(document);
        } else {
            setEditingDocument(null);
            setFormData({
                userId: record.id,
                documentType: '',
                documentNumber: '',
                expiryDate: '',
                description: '',
            });
        }
        setSelectedFile(null);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingDocument(null);
        setSelectedFile(null);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            // Validate file type
            if (file.type !== 'application/pdf') {
                notify('Please upload a PDF file', { type: 'warning' });
                return;
            }
            // Validate file size (max 10MB)
            if (file.size > 10 * 1024 * 1024) {
                notify('File size must be less than 10MB', { type: 'warning' });
                return;
            }
            setSelectedFile(file);
        }
    };

    const handleSave = async () => {
        // Prevent multiple clicks
        if (saving) {
            return;
        }

        // Validate file upload is required for new documents
        if (!editingDocument && !selectedFile) {
            notify('Please upload a PDF file', { type: 'warning' });
            return;
        }

        setSaving(true);

        try {
            const formDataToSend = new FormData();

            // Add all form fields
            formDataToSend.append('userId', String(formData.userId));
            formDataToSend.append('documentType', formData.documentType);
            formDataToSend.append(
                'documentNumber',
                formData.documentNumber || ''
            );
            if (formData.expiryDate) {
                formDataToSend.append('expiryDate', formData.expiryDate);
            }
            if (formData.description) {
                formDataToSend.append('description', formData.description);
            }

            // Add fileName - use new file name if file is selected, otherwise keep existing fileName
            if (selectedFile) {
                formDataToSend.append('fileName', selectedFile.name);
            } else if (editingDocument && editingDocument.fileName) {
                formDataToSend.append('fileName', editingDocument.fileName);
            }

            // Add file if selected
            if (selectedFile) {
                formDataToSend.append('file', selectedFile);
            }

            // Get auth token
            const token = await feathersClient.authentication.getAccessToken();

            if (editingDocument) {
                // Update existing document
                const response = await fetch(
                    `${settings.apiUrl}/user-documents/${editingDocument.id}`,
                    {
                        method: 'PATCH',
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                        body: formDataToSend,
                    }
                );

                if (!response.ok) {
                    const errorData = await response.json();
                    console.error('Error updating document:', errorData);
                    throw new Error(
                        errorData.message || 'Failed to update document'
                    );
                }

                notify('Document updated successfully', { type: 'success' });
            } else {
                // Create new document
                const response = await fetch(
                    `${settings.apiUrl}/user-documents`,
                    {
                        method: 'POST',
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                        body: formDataToSend,
                    }
                );

                if (!response.ok) {
                    const errorData = await response.json();
                    console.error('Error creating document:', errorData);
                    throw new Error(
                        errorData.message || 'Failed to create document'
                    );
                }

                notify('Document created successfully', { type: 'success' });
            }

            handleCloseDialog();
            loadDocuments();
            refresh();
        } catch (error) {
            console.error('Error saving document:', error);
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : 'Error saving document';
            notify(errorMessage, { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [
        documentToDelete,
        setDocumentToDelete,
    ] = useState<UserDocument | null>(null);

    const handleDeleteClick = (document: UserDocument) => {
        setDocumentToDelete(document);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!documentToDelete) return;

        try {
            await dataProvider.delete('user-documents', {
                id: documentToDelete.id!,
                previousData: documentToDelete,
            });
            notify('Document deleted successfully', { type: 'success' });
            loadDocuments();
            refresh();
        } catch (error) {
            notify('Error deleting document', { type: 'error' });
        } finally {
            setDeleteDialogOpen(false);
            setDocumentToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setDocumentToDelete(null);
    };

    const handleDownload = async (fileUrl: string, fileName: string) => {
        try {
            const response = await fetch(fileUrl);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Download failed:', error);
            notify('Failed to download file', { type: 'error' });
        }
    };

    const handleView = (fileUrl: string) => {
        window.open(fileUrl, '_blank');
    };

    return (
        <div>
            {showAddButton && (
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    className={classes.addButton}
                >
                    Add Document
                </Button>
            )}

            <Table className={classes.table}>
                <TableHead>
                    <TableRow>
                        <TableCell>Document Type</TableCell>
                        <TableCell>Document Number</TableCell>
                        <TableCell>Expiry Date</TableCell>
                        <TableCell>File</TableCell>
                        <TableCell>Verified</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {documents.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={7} align="center">
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    No documents found. Click "Add Document" to
                                    create one.
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ) : (
                        documents.map(doc => (
                            <TableRow key={doc.id}>
                                <TableCell>{doc.documentType}</TableCell>
                                <TableCell>{doc.documentNumber}</TableCell>
                                <TableCell>
                                    {doc.expiryDate ? (
                                        <DateField
                                            record={doc}
                                            source="expiryDate"
                                        />
                                    ) : (
                                        '-'
                                    )}
                                </TableCell>
                                <TableCell>
                                    {doc.fileUrl ? (
                                        <Box className={classes.actionButtons}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleView(doc.fileUrl!)
                                                }
                                                title="View PDF"
                                            >
                                                <ViewIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleDownload(
                                                        doc.fileUrl!,
                                                        doc.fileName ||
                                                            'document.pdf'
                                                    )
                                                }
                                                title="Download PDF"
                                            >
                                                <DownloadIcon fontSize="small" />
                                            </IconButton>
                                            <Typography variant="caption">
                                                {doc.fileName}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                        >
                                            No file
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {doc.isVerified ? (
                                        <Chip
                                            label="Verified"
                                            color="primary"
                                            size="small"
                                        />
                                    ) : (
                                        <Chip
                                            label="Not Verified"
                                            size="small"
                                        />
                                    )}
                                </TableCell>
                                <TableCell>
                                    {doc.createdAt ? (
                                        <DateField
                                            record={doc}
                                            source="createdAt"
                                            showTime
                                        />
                                    ) : (
                                        '-'
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Box className={classes.actionButtons}>
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                handleOpenDialog(doc)
                                            }
                                            color="primary"
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                handleDeleteClick(doc)
                                            }
                                            color="secondary"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>

            {/* Add/Edit Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {editingDocument ? 'Edit Document' : 'Add Document'}
                </DialogTitle>
                <DialogContent>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Document Type</InputLabel>
                        <Select
                            value={formData.documentType}
                            onChange={e =>
                                setFormData({
                                    ...formData,
                                    documentType: e.target.value as string,
                                })
                            }
                        >
                            {documentTypes.map(type => (
                                <MenuItem key={type.id} value={type.name}>
                                    {type.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        margin="normal"
                        label="Document Number"
                        value={formData.documentNumber}
                        onChange={e =>
                            setFormData({
                                ...formData,
                                documentNumber: e.target.value,
                            })
                        }
                    />

                    <TextField
                        fullWidth
                        margin="normal"
                        label="Expiry Date"
                        type="date"
                        value={formData.expiryDate}
                        onChange={e =>
                            setFormData({
                                ...formData,
                                expiryDate: e.target.value,
                            })
                        }
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />

                    <TextField
                        fullWidth
                        margin="normal"
                        label="Description"
                        multiline
                        rows={3}
                        value={formData.description}
                        onChange={e =>
                            setFormData({
                                ...formData,
                                description: e.target.value,
                            })
                        }
                    />

                    <Box mt={2}>
                        <Typography variant="body2" gutterBottom>
                            {editingDocument
                                ? 'Replace PDF File (Optional)'
                                : 'Upload PDF'}{' '}
                            {!editingDocument && (
                                <span style={{ color: 'red' }}>*</span>
                            )}
                        </Typography>
                        {editingDocument &&
                            editingDocument.fileName &&
                            !selectedFile && (
                                <Box
                                    mb={1}
                                    p={1}
                                    bgcolor="grey.100"
                                    borderRadius={1}
                                >
                                    <Typography
                                        variant="caption"
                                        display="block"
                                    >
                                        <strong>Current File:</strong>{' '}
                                        {editingDocument.fileName}
                                    </Typography>
                                </Box>
                            )}
                        <input
                            accept="application/pdf"
                            className={classes.fileInput}
                            id="document-file-upload"
                            type="file"
                            onChange={handleFileChange}
                        />
                        <label htmlFor="document-file-upload">
                            <Button
                                variant="outlined"
                                component="span"
                                className={classes.uploadButton}
                                color={selectedFile ? 'primary' : 'default'}
                            >
                                {selectedFile
                                    ? `New File Selected: ${selectedFile.name}`
                                    : editingDocument
                                    ? 'Choose New File'
                                    : 'Upload PDF'}
                            </Button>
                        </label>
                        {selectedFile && (
                            <Typography
                                variant="caption"
                                display="block"
                                color="primary"
                            >
                                <strong>New file:</strong> {selectedFile.name} -
                                Size: {(selectedFile.size / 1024).toFixed(2)} KB
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        color="primary"
                        variant="contained"
                        disabled={!formData.documentType || saving}
                        startIcon={
                            saving ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : null
                        }
                        style={{
                            minWidth: '100px',
                            opacity: saving ? 0.7 : 1,
                        }}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center">
                        <DeleteIcon color="error" style={{ marginRight: 8 }} />
                        Delete Document
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Are you sure you want to delete this document?
                    </Typography>

                    {documentToDelete && (
                        <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
                            <Typography
                                variant="subtitle2"
                                color="textSecondary"
                            >
                                Document Details:
                            </Typography>
                            <Box mt={1}>
                                <Typography variant="body2">
                                    <strong>Type:</strong>{' '}
                                    {documentToDelete.documentType}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Number:</strong>{' '}
                                    {documentToDelete.documentNumber || 'N/A'}
                                </Typography>
                                {documentToDelete.fileName && (
                                    <Typography variant="body2">
                                        <strong>File:</strong>{' '}
                                        {documentToDelete.fileName}
                                    </Typography>
                                )}
                                {documentToDelete.expiryDate && (
                                    <Typography variant="body2">
                                        <strong>Expiry Date:</strong>{' '}
                                        {documentToDelete.expiryDate}
                                    </Typography>
                                )}
                                {documentToDelete.description && (
                                    <Typography variant="body2">
                                        <strong>Description:</strong>{' '}
                                        {documentToDelete.description}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}

                    <Box mt={3}>
                        <Typography variant="body2" color="error">
                            <strong>Warning:</strong> This action cannot be
                            undone. The document and its associated file will be
                            permanently deleted.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>Cancel</Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="secondary"
                        variant="contained"
                        startIcon={<DeleteIcon />}
                    >
                        Delete Document
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default UserDocumentsList;
